import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getUserBookings, cancelBooking, deleteBooking, type Booking } from '../../services/booking.api';
import {
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  MapPin,
  Loader2,
  AlertCircle,
  ArrowRight,
  Wrench,
  Package,
  ChevronRight,
  CalendarClock,
  Search,
  Trash2,
} from 'lucide-react';
import { getHeroClass } from '../../lib/design-system';
import { Button, Input } from '../../components/ui';
import { cn } from '../../lib/utils';
import { getBookingDetailRoute, getOrderDetailRoute } from '../../config/routes';
import { SmartBadge } from '../../components/orders-bookings';
import { bookingTitle } from '../../lib/bookingLabel';

const statusConfig = {
  pending: {
    icon: Clock,
    label: 'Pending',
    badgeColor: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/20 dark:text-yellow-400 dark:border-yellow-900',
  },
  confirmed: {
    icon: CheckCircle2,
    label: 'Confirmed',
    badgeColor: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900',
  },
  in_progress: {
    icon: Wrench,
    label: 'In Progress',
    badgeColor: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/20 dark:text-purple-400 dark:border-purple-900',
  },
  completed: {
    icon: CheckCircle2,
    label: 'Completed',
    badgeColor: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-900',
  },
  cancelled: {
    icon: XCircle,
    label: 'Cancelled',
    badgeColor: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-900',
  },
};

/** Booking thumbnail — shows the asset image, falls back to a wrench icon. */
function BookingThumb({ src, alt }: { src: string | null; alt: string }) {
  const [failed, setFailed] = useState(false);
  const showImg = src && !failed;
  return (
    <div className="h-20 w-20 rounded-xl bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden border">
      {showImg ? (
        <img src={src} alt={alt} className="h-full w-full object-contain p-1.5" onError={() => setFailed(true)} />
      ) : (
        <Wrench className="h-8 w-8 text-muted-foreground/70" />
      )}
    </div>
  );
}

export function BookingsPage() {
  const { t } = useTranslation('bookings');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all'); // New: date filter
  const [confirmDelete, setConfirmDelete] = useState<Booking | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    loadBookings();
  }, []);
  
  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserBookings();
      setBookings(data);
    } catch (err: unknown) {
      console.error('Failed to load bookings:', err);
      const errorMessage = err instanceof Error ? err.message : ((err as Record<string, unknown>)?.data as Record<string, unknown>)?.message || ((err as Record<string, unknown>)?.data as Record<string, unknown>)?.error || 'Failed to load bookings';
      setError(errorMessage as string);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancelBooking = async (bookingId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(t('confirm.cancel'))) return;
    
    try {
      setCancellingId(bookingId);
      await cancelBooking(bookingId);
      await loadBookings();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : ((err as Record<string, unknown>)?.data as Record<string, unknown>)?.error || ((err as Record<string, unknown>)?.data as Record<string, unknown>)?.message || 'Failed to cancel booking';
      alert(errorMessage);
    } finally {
      setCancellingId(null);
    }
  };

  const handleDeleteBooking = async () => {
    if (!confirmDelete) return;
    try {
      setDeletingId(confirmDelete.id);
      await deleteBooking(confirmDelete.id);
      setBookings((prev) => prev.filter((b) => b.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : ((err as Record<string, unknown>)?.data as Record<string, unknown>)?.error || ((err as Record<string, unknown>)?.data as Record<string, unknown>)?.message || 'Failed to delete booking';
      alert(errorMessage);
    } finally {
      setDeletingId(null);
    }
  };

  // Filter bookings
  const filteredBookings = bookings.filter((booking) => {
    const matchesSearch =
      booking.serviceType.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (booking.home?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (booking.diagnosisIssue || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;

    // Date filter logic
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const bookingDate = new Date(booking.scheduledDate);
      const now = new Date();
      const dayInMs = 24 * 60 * 60 * 1000;

      switch (dateFilter) {
        case 'recent':
          // Last 7 days
          matchesDate = (now.getTime() - bookingDate.getTime()) <= 7 * dayInMs;
          break;
        case 'thisMonth':
          // Current month
          matchesDate = bookingDate.getMonth() === now.getMonth() && bookingDate.getFullYear() === now.getFullYear();
          break;
        case 'last30Days':
          // Last 30 days
          matchesDate = (now.getTime() - bookingDate.getTime()) <= 30 * dayInMs;
          break;
        case 'last90Days':
          // Last 90 days
          matchesDate = (now.getTime() - bookingDate.getTime()) <= 90 * dayInMs;
          break;
        case 'upcoming':
          // Future bookings only
          matchesDate = bookingDate > now;
          break;
        default:
          matchesDate = true;
      }
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  // Get stats
  const upcomingCount = bookings.filter(b => 
    new Date(b.scheduledDate) > new Date() && (b.status === 'pending' || b.status === 'confirmed')
  ).length;
  const completedCount = bookings.filter(b => b.status === 'completed').length;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
          <div>
            <h3 className="font-medium text-lg">{t('loading.title')}</h3>
            <p className="text-sm text-muted-foreground mt-1">{t('loading.subtitle')}</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-6 bg-background">
        <div className="text-center space-y-4 max-w-md">
          <div className="h-16 w-16 rounded-xl bg-red-50 dark:bg-red-950/20 flex items-center justify-center mx-auto border border-red-200 dark:border-red-900">
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold mb-2">{t('error.title')}</h2>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
          <Button onClick={loadBookings} className="mt-4">
            <ArrowRight className="h-4 w-4 mr-2" />
            {t('button.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      {/* ── Header Section ── */}
      <section className="bg-gradient-to-b from-background to-muted/20 border-b">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
          <div className="mb-6">
            <h1 className={getHeroClass('main')}>{t('title')}</h1>
            <p className="text-muted-foreground">
              {t('subtitle')}
            </p>
          </div>

          {/* Stats and Search row */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            {/* Stats */}
            <div className="flex gap-3 flex-wrap">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm bg-background">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{filteredBookings.length} {t('stats.total')}</span>
              </div>
              {upcomingCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm bg-[hsl(var(--brand-primary)/0.07)] border-[hsl(var(--brand-primary)/0.25)]">
                  <Clock className="h-4 w-4 text-[hsl(var(--brand-primary))]" />
                  <span className="font-medium text-[hsl(var(--brand-primary))]">{t('stats.upcoming', { count: upcomingCount })}</span>
                </div>
              )}
              {completedCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900">
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="font-medium text-green-700 dark:text-green-400">{t('stats.completed', { count: completedCount })}</span>
                </div>
              )}
            </div>

            {/* Search & Filter */}
            <div className="flex gap-2 w-full sm:w-auto flex-wrap">
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('search')}
                  className="pl-10 h-10 bg-background"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-10 px-3 rounded-lg border bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">{t('filter.allStatus')}</option>
                <option value="pending">{t('filter.pending')}</option>
                <option value="confirmed">{t('filter.confirmed')}</option>
                <option value="in_progress">{t('filter.inProgress')}</option>
                <option value="completed">{t('filter.completed')}</option>
                <option value="cancelled">{t('filter.cancelled')}</option>
              </select>

              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="h-10 px-3 rounded-lg border bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">All Time</option>
                <option value="upcoming">Upcoming</option>
                <option value="recent">Last 7 Days</option>
                <option value="thisMonth">This Month</option>
                <option value="last30Days">Last 30 Days</option>
                <option value="last90Days">Last 90 Days</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bookings List ── */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
        {filteredBookings.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-muted mb-4">
              <CalendarClock className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-2">
              {searchQuery || statusFilter !== 'all' ? t('empty.noResults') : t('empty.noBookings')}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {searchQuery || statusFilter !== 'all' 
                ? t('empty.adjustSearch')
                : t('empty.description')
              }
            </p>
            {(searchQuery || statusFilter !== 'all') && (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                }}
              >
                {t('button.clearFilters')}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredBookings.map((booking) => {
              const status = statusConfig[booking.status as keyof typeof statusConfig] || statusConfig.pending;
              const StatusIcon = status.icon;
              const scheduleDate = new Date(booking.scheduledDate);
              const isUpcoming = scheduleDate > new Date();
              const canCancel = booking.status === 'pending' || booking.status === 'confirmed';

              return (
                <div
                  key={booking.id}
                  onClick={() => navigate(getBookingDetailRoute(booking.id))}
                  className="group flex gap-4 rounded-2xl border bg-card p-4 sm:p-5 cursor-pointer transition-all hover:shadow-md hover:border-[hsl(var(--brand-primary)/0.4)]"
                >
                  {/* Asset image / icon */}
                  <BookingThumb src={booking.asset?.imageUrl || null} alt={booking.asset?.name || booking.serviceType} />

                  {/* Content */}
                  <div className="flex-1 min-w-0 flex flex-col gap-2.5">
                    {/* Row 1: status/title + fee */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={cn(
                            "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border",
                            status.badgeColor
                          )}>
                            <StatusIcon className="h-3 w-3" />
                            {t(`status.${booking.status}`)}
                          </span>
                          {isUpcoming && (booking.status === 'pending' || booking.status === 'confirmed') && (
                            <SmartBadge variant="today" size="small" />
                          )}
                        </div>
                        <h3 className="font-semibold text-sm sm:text-base leading-snug line-clamp-1 group-hover:text-primary transition-colors">
                          {bookingTitle(booking)}
                        </h3>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                          {booking.home && (
                            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{booking.home.name}</span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {scheduleDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {scheduleDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                      {booking.serviceFee ? (
                        <div className="text-right flex-shrink-0">
                          <div className="font-bold text-base sm:text-lg leading-none">${booking.serviceFee.toFixed(2)}</div>
                          <div className="text-[11px] text-muted-foreground mt-1">{t('serviceFee')}</div>
                        </div>
                      ) : null}
                    </div>

                    {/* Diagnosed issue */}
                    {booking.diagnosisIssue && (
                      <div className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-1">{booking.diagnosisIssue}</span>
                      </div>
                    )}

                    {/* Footer: parts chip + actions */}
                    <div className="flex items-center justify-between gap-2 min-h-[1.25rem]">
                      {booking.relatedOrders && booking.relatedOrders.length > 0 ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(getOrderDetailRoute(booking.relatedOrders![0].orderId)); }}
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-900 text-[11px] font-medium text-indigo-700 dark:text-indigo-300 hover:shadow-sm transition-all"
                        >
                          <Package className="h-3.5 w-3.5" />
                          {t('partsOrdered', { count: booking.relatedOrders.length })}
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      ) : <span />}

                      <div className="flex items-center gap-3 flex-shrink-0">
                        {canCancel && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCancelBooking(booking.id, e); }}
                            disabled={cancellingId === booking.id}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-destructive hover:underline disabled:opacity-50"
                          >
                            {cancellingId === booking.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <><XCircle className="h-3.5 w-3.5" />{t('button.cancel')}</>
                            )}
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDelete(booking); }}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          title="Delete booking"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </button>
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary">
                          {t('button.viewDetails')}
                          <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <>
          <button
            type="button"
            aria-label="Close dialog"
            className="fixed inset-0 bg-black/50 z-50 cursor-default"
            onClick={() => !deletingId && setConfirmDelete(null)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm p-6 rounded-2xl bg-background border shadow-xl">
            <h3 className="text-base font-medium mb-1.5">Delete booking?</h3>
            <p className="text-sm text-muted-foreground mb-5">
              <strong>{bookingTitle(confirmDelete)}</strong> will be permanently deleted. This
              cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmDelete(null)}
                disabled={!!deletingId}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteBooking}
                disabled={!!deletingId}
                className="gap-2"
              >
                {deletingId && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Delete
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
